export interface Perk {
  id: string;
  slug: string;
  partnerName: string;
  category: string;
  perkSummary: string;
  residentBenefit: string;
  description: string;
  type: 'resident-benefit' | 'sponsor-supported' | 'future-partner';
  status: 'active' | 'coming-soon' | 'removed';
  cta: string;
  tags: string[];
  isMockExample?: boolean;
}

export const perkCategories = [
  'All',
  'Nightlife',
  'Dining',
  'Wellness',
  'Services',
  'Luxury & VIP',
  'Home Services',
];

export const mockPerks: Perk[] = [
  {
    id: 'perk-1',
    slug: 'vip-nightlife-host-access',
    partnerName: 'VIP Nightlife Host — Example Partner',
    category: 'Nightlife',
    perkSummary: 'Complimentary guest list access and priority table inquiry for select Strip venues.',
    residentBenefit: 'Skip the general admission line and access dedicated host services at participating venues.',
    description: 'This is an example of the type of nightlife perk that could be available to Panorama residents through a verified host partnership. Panorama\'s resident profile — affluent, local, repeat visitors — makes the building an attractive partner for premium nightlife hosts. This slot is reserved for a future verified partner.',
    type: 'future-partner',
    status: 'coming-soon',
    cta: 'Coming Soon',
    tags: ['Nightlife', 'VIP', 'Guest List', 'Strip'],
    isMockExample: true,
  },
  {
    id: 'perk-2',
    slug: 'wellness-partner-example',
    partnerName: 'Wellness Studio — Example Partner',
    category: 'Wellness',
    perkSummary: 'Resident discount on monthly memberships and complimentary first class.',
    residentBenefit: 'Exclusive resident pricing and a free introductory session for verified Panorama residents.',
    description: 'This is an example of a wellness partnership perk. A local yoga studio, Pilates studio, or recovery center could offer Panorama residents a preferred rate in exchange for community visibility. This slot is reserved for a future verified wellness partner.',
    type: 'future-partner',
    status: 'coming-soon',
    cta: 'Coming Soon',
    tags: ['Wellness', 'Fitness', 'Studio', 'Discount'],
    isMockExample: true,
  },
  {
    id: 'perk-3',
    slug: 'dining-perk-example',
    partnerName: 'Restaurant Partner — Example',
    category: 'Dining',
    perkSummary: 'Priority reservations and a complimentary welcome course for Panorama residents.',
    residentBenefit: 'Resident-only reservation access and a small-plates welcome from the kitchen.',
    description: 'This is an example of a dining perk structure. A local restaurant partner could offer Panorama residents priority booking and a resident recognition benefit. This slot is reserved for a future verified dining partner.',
    type: 'future-partner',
    status: 'coming-soon',
    cta: 'Coming Soon',
    tags: ['Dining', 'Reservations', 'Restaurant'],
    isMockExample: true,
  },
  {
    id: 'perk-4',
    slug: 'concierge-car-service',
    partnerName: 'Premium Car Service — Example Partner',
    category: 'Services',
    perkSummary: 'Preferred resident rate on airport transfers and Strip transportation.',
    residentBenefit: 'Discounted flat-rate transfers for verified residents, with building pickup coordination.',
    description: 'This is an example of a service perk. A premium car service or black car company could offer Panorama residents a preferred rate for airport runs and Strip transportation. This slot is reserved for a future verified service partner.',
    type: 'future-partner',
    status: 'coming-soon',
    cta: 'Coming Soon',
    tags: ['Transportation', 'Car Service', 'Airport', 'Convenience'],
    isMockExample: true,
  },
  {
    id: 'perk-5',
    slug: 'luxury-vip-access-example',
    partnerName: 'Luxury Experience Partner — Example',
    category: 'Luxury & VIP',
    perkSummary: 'Curated VIP access to select Las Vegas experiences and private events.',
    residentBenefit: 'Resident-only invitations to private events, preview access, and curated experience packages.',
    description: 'This is an example of a luxury access perk. A concierge service or experience curator could offer Panorama residents access to private events, exclusive previews, and curated Las Vegas experiences. This slot is reserved for a future verified luxury partner.',
    type: 'future-partner',
    status: 'coming-soon',
    cta: 'Coming Soon',
    tags: ['Luxury', 'VIP', 'Events', 'Exclusive'],
    isMockExample: true,
  },
  {
    id: 'perk-6',
    slug: 'home-services-partner-example',
    partnerName: 'Home Services Partner — Example',
    category: 'Home Services',
    perkSummary: 'Preferred resident pricing on cleaning, maintenance, and home organization services.',
    residentBenefit: 'Vetted service providers with resident-preferred rates and building-familiar logistics.',
    description: 'This is an example of a home services perk. A cleaning service, handyman company, or home organization provider could offer Panorama residents preferred pricing and building-familiar service. This slot is reserved for a future verified home services partner.',
    type: 'future-partner',
    status: 'coming-soon',
    cta: 'Coming Soon',
    tags: ['Home Services', 'Cleaning', 'Maintenance', 'Convenience'],
    isMockExample: true,
  },
  {
    id: 'perk-7',
    slug: 'resident-benefit-usps-informed',
    partnerName: 'USPS Informed Delivery',
    category: 'Services',
    perkSummary: 'Free mail scanning service — see your incoming mail before it arrives.',
    residentBenefit: 'Daily email previews of your incoming mail. Especially useful for second-home owners and frequent travelers.',
    description: 'USPS Informed Delivery is a free service that sends you daily email scans of your incoming mail. It\'s not a partner perk — it\'s a free government service that every Panorama resident should be using. Sign up at usps.com.',
    type: 'resident-benefit',
    status: 'active',
    cta: 'Learn More',
    tags: ['Mail', 'Free', 'USPS', 'Second Home'],
    isMockExample: false,
  },
];

export function getPerkBySlug(slug: string): Perk | undefined {
  return mockPerks.find((p) => p.slug === slug);
}
